import React from "react";
import Container from "@/components/marketing/Container";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/marketing/Reveal";

const PACKAGES = [
  {
    category: "Investor-Ready",
    name: "Finance Credibility Package",
    popular: false,
    description: "Get your story on eight high-authority finance publications that build credibility with investors, clients, and partners.",
    features: [
      "8 guaranteed placements, all Google-indexed",
      "High-authority finance sites (DA 67 to 93)",
      "Built for fintech, crypto, and investor audiences",
    ],
    price: "$799",
    compareAt: "$990",
    savings: "$191",
    anchor: "/packages#finance-credibility",
  },
  {
    category: "Most Popular",
    name: "US Visibility Package",
    popular: true,
    description: "Guaranteed placements on flagship US outlets plus newswire syndication. Broad, credible US coverage.",
    features: [
      "3 flagship US placements plus wire syndication",
      "PR Newswire distribution to 500+ outlets",
      "Journalist outreach included",
    ],
    price: "$949",
    compareAt: "$1,489",
    savings: "$540",
    anchor: "/packages#us-visibility",
  },
  {
    category: "SEO Power",
    name: "DoFollow Authority Package",
    popular: false,
    description: "Real DoFollow backlinks from established news publications, DA 57 to 92, anchored by MSN. Built to grow domain authority.",
    features: [
      "DoFollow links from real news sites (DA 57 to 92)",
      "Anchored by MSN, a top global news domain",
      "Indexed on Google at publication",
    ],
    price: "$699",
    compareAt: "$939",
    savings: "$240",
    anchor: "/packages#dofollow-authority",
  },
];

export default function TopSellers() {
  return (
    <section className="py-11 lg:py-12 border-t border-[rgba(28,28,32,0.06)]">
      <Container>
        {/* Heading */}
        <Reveal>
          <div className="text-center mb-10">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#6d28d9] mb-3.5">Curated Packages</div>
            <h2 className="font-heading text-[clamp(32px,4vw,46px)] font-[650] tracking-[-0.03em] leading-[1.06] mb-3.5 text-heading-gradient">
              The mix is already figured out.
            </h2>
            <p className="text-[16px] leading-[1.55] text-[#6c6c74] max-w-[480px] mx-auto">
              Each package is put together around your sector, region, or goal, and priced below buying the channels separately.
            </p>
          </div>
        </Reveal>

        {/* Package cards */}
        <div className="grid md:grid-cols-3 gap-[22px] items-start">
          {PACKAGES.map((pkg, index) => (
            <Reveal variant="card" delay={index * 0.08} key={pkg.name}>
              <article
                className={`glass-card relative flex flex-col p-7 lg:p-8 ${pkg.popular ? "glass-featured" : ""}`}
              >
              {pkg.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-[0.12em] text-white bg-brand-gradient px-4 py-1.5 rounded-full whitespace-nowrap" style={{ boxShadow: "0 6px 16px rgba(124,58,237,0.35)" }}>
                  ★ Most Popular
                </div>
              )}
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#7c3aed] mb-2.5">{pkg.category}</div>
              <h3 className="text-[22px] font-[650] tracking-[-0.02em] mb-1.5">{pkg.name}</h3>
              <p className="text-[13.5px] leading-[1.5] text-[#6c6c74] mb-4.5">{pkg.description}</p>

              {/* Features */}
              <div className="flex flex-col gap-2.5 mb-5">
                {pkg.features.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <span className="check-circle mt-[1px]">✓</span>
                    <span className="text-[13px] text-[#55555c] leading-[1.45]">{f}</span>
                  </div>
                ))}
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-2.5 mb-5">
                <span className="text-[34px] font-bold tracking-[-0.025em] whitespace-nowrap">{pkg.price}</span>
                <span className="text-[14px] text-[#b6b6bd] line-through whitespace-nowrap">{pkg.compareAt}</span>
                <span className="text-[12px] font-semibold text-[#6d28d9] whitespace-nowrap">Save {pkg.savings}</span>
              </div>

              <Link
                to={pkg.anchor}
                className={`mt-auto block text-center text-[14px] font-semibold py-[13px] rounded-[14px] text-white whitespace-nowrap transition-opacity hover:opacity-90 ${pkg.popular ? "bg-brand-gradient" : "bg-dark-pill"}`}
                style={pkg.popular ? { boxShadow: "0 8px 20px rgba(124,58,237,0.35)" } : { boxShadow: "0 6px 16px rgba(28,28,32,0.18)" }}
              >
                Details
              </Link>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="text-center mt-9">
          <Link to="/packages"
            className="inline-block text-[14px] font-semibold px-6 py-3 rounded-xl bg-[rgba(139,92,246,0.09)] border border-[rgba(139,92,246,0.2)] text-[#6d28d9] whitespace-nowrap transition-colors hover:bg-[rgba(139,92,246,0.16)]">
            See all packages →
          </Link>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}